import "./styles/index.css";

import React, {
  Suspense,
  lazy,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactGA from "react-ga";
import { Box, Container, useTheme } from "@chakra-ui/react";

import HeaderBar from "./components/HeaderBar";
import IntroHeroBlock from "./components/IntroHeroBlock";
import {
  ApiResponse,
  CaseFilters,
  CaseRecord,
  ColumnConfig,
  ColumnKey,
  FilterOptions,
  PAGE_SIZE_OPTIONS,
  SortOrder,
} from "./types/App.types";
import {
  ALL_FILTER_VALUE,
  buildFilterOptions,
  displayValue,
  emptyFilters,
  filterCaseRows,
  getColumnLabel as getColumnLabelHelper,
  paginateRows,
  sortCaseRows,
} from "./utils/App.helpers";

const SearchControls = lazy(() => import("./components/SearchControls"));
const DataTable = lazy(() => import("./components/DataTable"));
const Footer = lazy(() => import("./components/Footer"));

const apiHost = process.env.REACT_APP_API_HOST!;
const apiPath = process.env.REACT_APP_API_PATH || "/api/v1/public/cases";

if (process.env.NODE_ENV === "production") {
  ReactGA.initialize("G-LGY928V1DM", { debug: false });
  ReactGA.pageview(window.location.pathname + window.location.search);
}

const COLUMNS: ColumnConfig[] = [
  { key: "name", label: "Name", width: "12rem", align: "left" },
  { key: "city", label: "City", width: "11rem", align: "left" },
  { key: "state", label: "State", width: "8rem", align: "left" },
  {
    key: "sportAffiliations",
    label: "Sport Affiliation(s)",
    width: "15rem",
    align: "left",
  },
  { key: "misconduct", label: "Misconduct", width: "18rem", align: "left" },
  { key: "actionTaken", label: "Action Taken", width: "15rem", align: "left" },
  {
    key: "additionalDetail",
    label: "Additional Detail",
    width: "20rem",
    align: "left",
  },
  {
    key: "dateOfIssuance",
    label: "Date of Issuance",
    width: "10rem",
    align: "left",
  },
  {
    key: "adjudicatingBody",
    label: "Adjudicating Body",
    width: "15rem",
    align: "left",
  },
];

const EMPTY_FILTER_OPTIONS: FilterOptions = {
  cities: [],
  states: [],
  sportAffiliations: [],
  adjudicatingBodies: [],
};

const FILTER_LABELS: Record<keyof CaseFilters, string> = {
  city: "City",
  state: "State",
  sportAffiliation: "Sport Affiliation",
  adjudicatingBody: "Adjudicating Body",
};

const App: React.FC = () => {
  const theme = useTheme();
  const blue50 = (theme.colors as any)?.blue?.[50] || "#CADFED";

  const [allRows, setAllRows] = useState<CaseRecord[]>([]);
  const [filterOptions, setFilterOptions] =
    useState<FilterOptions>(EMPTY_FILTER_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [lastModified, setLastModified] = useState<string>("");

  const [searchInput, setSearchInput] = useState("");
  const searchTerm = useDeferredValue(searchInput);
  const [filters, setFilters] = useState<CaseFilters>(() => emptyFilters());
  const [sortOrder, setSortOrder] = useState<SortOrder>({
    name: "dateOfIssuance",
    direction: "desc",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    PAGE_SIZE_OPTIONS[1] ?? 10,
  );
  const [liveMessage, setLiveMessage] = useState("");

  const mainHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const tableTitleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      if (!apiHost) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const apiUrl = `https://${apiHost}${apiPath}`;
        const response = await fetch(apiUrl, {
          signal: controller.signal,
        });
        const data = (await response.json()) as ApiResponse;
        const nextRows = data.data ?? [];

        setAllRows(nextRows);
        setFilterOptions(data.filters ?? buildFilterOptions(nextRows));
        setLastModified(data.lastModified ?? "");
        setIsLoading(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn("Failed to load cases", error);
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      controller.abort();
    };
  }, []);

  const announce = useCallback((message: string) => {
    setLiveMessage(message);
  }, []);

  const focusTableTitle = useCallback(() => {
    if (tableTitleRef.current) {
      tableTitleRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filters]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => value !== "all").length,
    [filters],
  );

  const filteredRows = useMemo(
    () => filterCaseRows(allRows, searchTerm, filters),
    [allRows, filters, searchTerm],
  );

  const sortedRows = useMemo(
    () => sortCaseRows(filteredRows, sortOrder),
    [filteredRows, sortOrder],
  );

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = useMemo(
    () => paginateRows(sortedRows, currentPage, rowsPerPage),
    [currentPage, rowsPerPage, sortedRows],
  );
  const currentRangeStart =
    sortedRows.length === 0 ? 0 : currentPage * rowsPerPage + 1;
  const currentRangeEnd =
    sortedRows.length === 0
      ? 0
      : currentPage * rowsPerPage + visibleRows.length;

  const describeResultWindow = useCallback(
    (totalResults: number, pageSize: number) => {
      if (totalResults === 0) {
        return "No matching results.";
      }

      const rangeEnd = Math.min(totalResults, pageSize);
      return `Showing results 1 to ${rangeEnd} of ${totalResults}.`;
    },
    [],
  );

  const getColumnLabel = useCallback(
    (key: ColumnKey) => getColumnLabelHelper(COLUMNS, key),
    [],
  );

  const handleSort = useCallback(
    (key: ColumnKey) => {
      setSortOrder((prev) => {
        const isSame = prev.name === key;
        const nextDirection: SortOrder["direction"] =
          isSame && prev.direction === "asc" ? "desc" : "asc";

        const label = getColumnLabel(key);
        announce(
          `Sorted by ${label} in ${
            nextDirection === "asc" ? "ascending" : "descending"
          } order. Showing results ${currentRangeStart} to ${currentRangeEnd} of ${
            sortedRows.length
          }.`,
        );
        setTimeout(focusTableTitle, 0);

        return {
          name: key,
          direction: nextDirection,
        };
      });
    },
    [
      announce,
      currentRangeEnd,
      currentRangeStart,
      focusTableTitle,
      getColumnLabel,
      sortedRows.length,
    ],
  );

  const handleChangePage = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(0, Math.min(nextPage, pageCount - 1));
      setPage(clamped);
      const nextRangeStart =
        sortedRows.length === 0 ? 0 : clamped * rowsPerPage + 1;
      const nextRangeEnd =
        sortedRows.length === 0
          ? 0
          : Math.min(sortedRows.length, clamped * rowsPerPage + rowsPerPage);
      announce(
        `Page ${clamped + 1} of ${pageCount}. Showing results ${nextRangeStart} to ${nextRangeEnd} of ${sortedRows.length}.`,
      );
      setTimeout(focusTableTitle, 0);
    },
    [announce, focusTableTitle, pageCount, rowsPerPage, sortedRows.length],
  );

  const handleChangeRowsPerPage = useCallback(
    (value: number) => {
      setRowsPerPage(value);
      setPage(0);
      const nextRangeEnd = Math.min(sortedRows.length, value);
      announce(
        `Rows per page set to ${value}. Showing results ${
          nextRangeEnd === 0 ? 0 : 1
        } to ${nextRangeEnd} of ${sortedRows.length}.`,
      );
      setTimeout(focusTableTitle, 0);
    },
    [announce, focusTableTitle, sortedRows.length],
  );

  const handleFilterChange = useCallback(
    (name: keyof CaseFilters, value: string) => {
      const nextFilters = {
        ...filters,
        [name]: value,
      };
      const nextResultCount = filterCaseRows(
        allRows,
        searchTerm,
        nextFilters,
      ).length;

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
      announce(
        value === ALL_FILTER_VALUE
          ? `${FILTER_LABELS[name]} filter cleared. ${describeResultWindow(
              nextResultCount,
              rowsPerPage,
            )}`
          : `${FILTER_LABELS[name]} filter set to ${displayValue(
              value,
            )}. ${describeResultWindow(nextResultCount, rowsPerPage)}`,
      );
    },
    [allRows, announce, describeResultWindow, filters, rowsPerPage, searchTerm],
  );

  const handleClearFilters = useCallback(() => {
    setFilters(emptyFilters());
    const nextResultCount = filterCaseRows(
      allRows,
      searchTerm,
      emptyFilters(),
    ).length;
    announce(
      `All filters cleared. ${describeResultWindow(nextResultCount, rowsPerPage)}`,
    );
  }, [allRows, announce, describeResultWindow, rowsPerPage, searchTerm]);

  return (
    <Box minH="100vh" bg={blue50}>
      <HeaderBar nowDate={lastModified} />

      <Box as="main" role="main" aria-labelledby="cdd-main-heading">
        <IntroHeroBlock mainHeadingRef={mainHeadingRef} />

        <Box py={{ base: 6, md: 10 }}>
          <Container maxW="96rem" px={{ base: 4, md: 6, xl: 8 }}>
            <Suspense fallback={<Box minH="14rem" />}>
              <SearchControls
                liveMessage={liveMessage}
                searchTerm={searchInput}
                setSearchTerm={setSearchInput}
                filters={filters}
                filterOptions={filterOptions}
                activeFilterCount={activeFilterCount}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />

              <DataTable
                isLoading={isLoading}
                tableTitleRef={tableTitleRef}
                sortOrder={sortOrder}
                handleSort={handleSort}
                rows={visibleRows}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                currentPage={currentPage}
                pageCount={pageCount}
                handleChangePage={handleChangePage}
                columns={COLUMNS}
                totalRows={sortedRows.length}
              />
            </Suspense>
          </Container>
        </Box>
      </Box>

      <Suspense fallback={<Box h="4rem" />}>
        <Footer />
      </Suspense>
    </Box>
  );
};

export default App;
